// Importing React libs
import { useCallback, useState, useEffect } from 'react';

const ZOOM_MAX = 2 ** 14;
const ZOOM_MIN = 1;

const useChartZoom = ([start, end], zoomPercent = 25) => {
  const zoomFactor = 100 / (100 - zoomPercent);
  const fullLength = end - start;
  const [zoom, setZoom] = useState(1);
  const [position, setPositionInternal] = useState(fullLength / 2);
  const [avoidNextLengthChangeReset, setOverrideReset] = useState(false);

  useEffect(() => {
    if (avoidNextLengthChangeReset) {
      setOverrideReset(false);
    } else {
      setZoom(1);
      setPositionInternal(fullLength / 2);
    }
  }, [fullLength]);

  // zoom in 50%
  const zoomIn = useCallback(
    () => setZoom((z) => Math.min(z * zoomFactor, ZOOM_MAX)),
    []
  );

  // zoom out 50%
  const zoomOut = () => {
    const newZoom = Math.max(zoom / zoomFactor, ZOOM_MIN);
    const newVisibleLength = fullLength / newZoom;
    const minPosition = start + newVisibleLength / 2;
    const maxPosition = end - newVisibleLength / 2;
    setZoom(newZoom);
    setPositionInternal(Math.max(Math.min(position, maxPosition), minPosition));
  };

  const reset = () => {
    setZoom(1);
    setPositionInternal(fullLength / 2);
  };

  const setExtent = ([start, end], length) => {
    const newLength = end - start;
    const newZoom = (length ?? fullLength) / newLength;
    const newPosition = (start + end) / 2;
    setZoom(Math.min(Math.max(newZoom, ZOOM_MIN), ZOOM_MAX));
    setPositionInternal(newPosition);
    setOverrideReset(typeof length === 'number');
  };

  const visibleLength = fullLength / zoom;
  const transformedExtent = [
    Math.max(start, position - visibleLength / 2),
    Math.min(end, position + visibleLength / 2),
  ];

  const setPosition = (newPosition) => {
    setPositionInternal(
      Math.max(
        visibleLength / 2,
        Math.min(
          newPosition,
          fullLength - visibleLength / 2,
        )
      )
    );
  };

  const translateStep = visibleLength * zoomPercent / 100 / 2;
  const panLeft = () => setPosition(position - translateStep);
  const panRight = () => setPosition(position + translateStep);

  return {
    position,
    transformedExtent,
    zoom,
    panLeft,
    panRight,
    reset,
    setExtent,
    setPosition,
    zoomIn,
    zoomOut,
  };
};

export default useChartZoom;
